import { useMemo, useCallback } from 'react';
import { format, getISOWeek, getQuarter, startOfWeek, startOfMonth, startOfQuarter, addDays, addWeeks, addMonths, addQuarters } from 'date-fns';
import { CHART_VIEWS } from './ItemAnalytics';
import { get } from 'lodash';

// Convert date to period string based on period type
export const dateToPeriodString = (date, periodType) => {
    switch (periodType) {
    case 'day':
        return format(date, 'yyyy-MM-dd');
    case 'week': {
        // getISOWeek returns 1-indexed week numbers (1–53), matching backend format
        // ISO weeks can cross year boundaries: e.g. Dec 29, 2025 is ISO week 0 of 2026
        const weekNum = getISOWeek(date) === 53 ? 52 : getISOWeek(date); // Handle edge case where some years have 53 weeks
        const year = (date.getMonth() === 11 && weekNum === 1) ? date.getFullYear() + 1 : date.getFullYear();
        return `${year}-W${weekNum}`;
    }
    case 'month':
        return format(date, 'yyyy-MM');
    case 'quarter': {
        const year = date.getFullYear();
        const quarter = getQuarter(date);
        return `${year}-Q${quarter}`;
    }
    default:
        return format(date, 'yyyy-MM-dd');
    }
};

// Convert period string to date range based on period type
export const periodToDateRange = (periodValue, periodType) => {
    switch (periodType) {
    case 'day': {
        return {
            startDate: periodValue,
            endDate: periodValue
        };
    }
    case 'week': {
        const match = periodValue.match(/(\d{4})-W(\d{1,2})/);
        if (match) {
            const year = parseInt(match[1], 10);
            const week = parseInt(match[2], 10); // Already ISO week number (1-indexed)
            // Calculate the start of the week (Monday)
            const jan1 = new Date(year, 0, 1);
            const daysToMonday = (week * 7) - jan1.getDay() + 1 - 7; // Adjust for Monday start
            const weekStart = new Date(year, 0, 1 + daysToMonday);
            const weekEnd = new Date(weekStart);
            weekEnd.setDate(weekStart.getDate() + 6); // Sunday
            return {
                startDate: format(weekStart, 'yyyy-MM-dd'),
                endDate: format(weekEnd, 'yyyy-MM-dd')
            };
        }
        break;
    }
    case 'month': {
        const match = periodValue.match(/(\d{4})-(\d{2})/);
        if (match) {
            const year = parseInt(match[1], 10);
            const month = parseInt(match[2], 10);
            const monthStart = new Date(year, month - 1, 1);
            const monthEnd = new Date(year, month, 0); // Last day of month
            return {
                startDate: format(monthStart, 'yyyy-MM-dd'),
                endDate: format(monthEnd, 'yyyy-MM-dd')
            };
        }
        break;
    }
    case 'quarter': {
        const match = periodValue.match(/(\d{4})-Q(\d)/);
        if (match) {
            const year = parseInt(match[1], 10);
            const quarter = parseInt(match[2], 10);
            const quarterStart = new Date(year, (quarter - 1) * 3, 1);
            const quarterEnd = new Date(year, quarter * 3, 0); // Last day of quarter
            return {
                startDate: format(quarterStart, 'yyyy-MM-dd'),
                endDate: format(quarterEnd, 'yyyy-MM-dd')
            };
        }
        break;
    }
    }
    
    // Fallback: return the period value as both start and end
    return {
        startDate: periodValue,
        endDate: periodValue
    };
};

// Compare two period values chronologically based on period type
export const comparePeriods = (periodA, periodB, periodType) => {
    if (!periodA || !periodB) return 0;
    
    switch (periodType) {
    case 'week': {
        const parseWeek = (period) => {
            const match = period.match(/(\d{4})-W(\d{1,2})/);
            return match ? { year: parseInt(match[1], 10), week: parseInt(match[2], 10) } : null;
        };
        
        const weekA = parseWeek(periodA);
        const weekB = parseWeek(periodB);
        
        if (!weekA || !weekB) return 0;
        
        if (weekA.year !== weekB.year) {
            return weekA.year - weekB.year;
        }
        return weekA.week - weekB.week;
    }
    case 'month': {
        const parseMonth = (period) => {
            const match = period.match(/(\d{4})-(\d{2})/);
            return match ? { year: parseInt(match[1], 10), month: parseInt(match[2], 10) } : null;
        };
        
        const monthA = parseMonth(periodA);
        const monthB = parseMonth(periodB);
        
        if (!monthA || !monthB) return 0;
        
        if (monthA.year !== monthB.year) {
            return monthA.year - monthB.year;
        }
        return monthA.month - monthB.month;
    }
    case 'quarter': {
        const parseQuarter = (period) => {
            const match = period.match(/(\d{4})-Q(\d)/);
            return match ? { year: parseInt(match[1], 10), quarter: parseInt(match[2], 10) } : null;
        };
        
        const quarterA = parseQuarter(periodA);
        const quarterB = parseQuarter(periodB);
        
        if (!quarterA || !quarterB) return 0;
        
        if (quarterA.year !== quarterB.year) {
            return quarterA.year - quarterB.year;
        }
        return quarterA.quarter - quarterB.quarter;
    }
    case 'day':
    default:
        return periodA.localeCompare(periodB);
    }
};

// Generate expected periods based on date range and period type
export const generateExpectedPeriods = (startDate, endDate, periodType) => {
    const periods = [];
    // Parse dates manually to avoid UTC conversion
    const parseLocalDate = (dateStr) => {
        if (typeof dateStr === 'string' && dateStr.includes('-')) {
            const [year, month, day] = dateStr.split('-').map(Number);
            return new Date(year, month - 1, day); // month is 0-indexed
        }
        return new Date(dateStr);
    };
    
    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);
    let current = new Date(start);

    // Adjust start date to period boundary using date-fns
    switch (periodType) {
    case 'week':
        current = startOfWeek(current, { weekStartsOn: 1 }); // Monday start
        break;
    case 'month':
        current = startOfMonth(current);
        break;
    case 'quarter':
        current = startOfQuarter(current);
        break;
    // 'day' needs no adjustment
    }

    while (current <= end) {
        periods.push(dateToPeriodString(current, periodType));
        
        // Move to next period using date-fns
        switch (periodType) {
        case 'day':
            current = addDays(current, 1);
            break;
        case 'week':
            current = addWeeks(current, 1);
            break;
        case 'month':
            current = addMonths(current, 1);
            break;
        case 'quarter':
            current = addQuarters(current, 1);
            break;
        }
    }

    return periods;
};

// Count how many periods are in a date range
export const countPeriodsInRange = (startDate, endDate, periodType) => {
    return generateExpectedPeriods(startDate, endDate, periodType).length;
};

// Calculate total of a key from time series data
export const getTotalFromTimeSeries = (timeSeries, key) => {
    if (!timeSeries || !Array.isArray(timeSeries)) return 0;
    return timeSeries.reduce((total, item) => {
        const value = item[key];
        return total + (typeof value === 'number' ? value : 0);
    }, 0);
};

// Calculate average of a key from time series data
export const getAverageFromTimeSeries = (timeSeries, key) => {
    if (!timeSeries || !Array.isArray(timeSeries) || timeSeries.length === 0) return 0;
    const total = getTotalFromTimeSeries(timeSeries, key);
    return total / timeSeries.length;
};

// Calculate average of a key from time series data, excluding zero values
export const getNonZeroAverageFromTimeSeries = (timeSeries, key) => {
    if (!timeSeries || !Array.isArray(timeSeries)) return 0;
    const nonZeroValues = timeSeries
        .map(item => item[key])
        .filter(value => typeof value === 'number' && value > 0);
    
    if (nonZeroValues.length === 0) return 0;
    const total = nonZeroValues.reduce((sum, value) => sum + value, 0);
    return total / nonZeroValues.length;
};

// Calculate standard deviation of a key from time series data
export const getStandardDeviationFromTimeSeries = (timeSeries, key, mean = null) => {
    if (!timeSeries || !Array.isArray(timeSeries) || timeSeries.length <= 1) return 0;
    
    const values = timeSeries
        .map(item => item[key])
        .filter(value => typeof value === 'number' && !isNaN(value));
    
    if (values.length <= 1) return 0;
    
    // Use provided mean or calculate it
    const average = mean !== null ? mean : getAverageFromTimeSeries(timeSeries, key);
    
    const variance = values.reduce((sum, value) => sum + Math.pow(value - average, 2), 0) / (values.length - 1);
    return Math.sqrt(variance);
};

// Generate complete time series with missing periods filled with zeros
export const generateCompleteTimeSeries = (rawData, period, startDate, endDate, defaultUsage = 0, isProjection = false) => {
    if (!rawData || !startDate || !endDate) return rawData || [];

    // Generate expected periods from the full date range
    const expectedPeriods = generateExpectedPeriods(startDate, endDate, period);

    // Create a map of existing data
    const dataMap = new Map();
    rawData.forEach(item => {
        dataMap.set(item.period, item);
    });

    // Fill in all expected periods with data or zeros
    return expectedPeriods.map(periodKey => {
        if (dataMap.has(periodKey) && !isProjection) {
            return dataMap.get(periodKey);
        } else {
            // Create zero data for missing period
            return {
                period: periodKey,
                period_start: null,
                period_end: null,
                num_cases: 0,
                used_quantity: defaultUsage,
                trashed_quantity: 0,
                run_quantity: 0,
                restock_quantity: 0,
                open_quantity: 0,
                hold_quantity: 0,
                isProjected: isProjection
            };
        }
    });
};

export function getChartData(completeTimeSeries, chartView) {
    const timeSeriesData = completeTimeSeries.map(item => ({
        x: item.period,
        period_start: item.period_start,
        period_end: item.period_end,
        cases: item.num_cases,
        used_quantity: item.used_quantity,
        trashed_quantity: item.trashed_quantity,
        run_quantity: item.run_quantity,
        restock_quantity: item.restock_quantity,
        open_quantity: item.open_quantity,
        hold_quantity: item.hold_quantity,
        isProjected: item.isProjected || false
    }));

    if (chartView === CHART_VIEWS.PICKLIST_VIEW) {
        return timeSeriesData.map(item => ({
            period: item.x,
            run: item.run_quantity || 0,
            restock: item.restock_quantity || 0,
            'open not used': item.trashed_quantity || 0,
            ...item
        }));
    } else if (chartView === CHART_VIEWS.INVENTORY_VIEW) {
        return [
            {
                id: 'total_picked',
                data: timeSeriesData.map(item => ({
                    x: item.x,
                    y: (item.hold_quantity || 0) + (item.open_quantity || 0),
                    ...item
                }))
            },
            {
                id: 'total_used',
                data: timeSeriesData.map(item => ({
                    x: item.x,
                    y: item.used_quantity || 0,
                    ...item
                }))
            }
        ];
    } else {
        return [
            {
                id: 'total_used',
                data: timeSeriesData.map(item => ({
                    x: item.x,
                    y: item.used_quantity || 0,
                    ...item
                }))
            }
        ];
    }
}

export function useChartData(time_series_data, chartView, period, dateRange) {
    // Transform data for nivo charts
    const chartData = useMemo(() => {
        if (!time_series_data) return [];
        
        // Generate complete time series with zero-filled gaps for full date range
        const completeTimeSeries = generateCompleteTimeSeries(
            time_series_data, 
            period, 
            dateRange?.startDate, 
            dateRange?.endDate
        );
        return getChartData(completeTimeSeries, chartView);
    }, [time_series_data, chartView, period, dateRange]);

    return chartData;
}